"use client";

import {
  AuthError,
  EmailAuthProvider,
  GoogleAuthProvider,
  User,
  createUserWithEmailAndPassword,
  getRedirectResult,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import {
  DocumentData,
  QueryDocumentSnapshot,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import {
  messageHasContactLeak,
  protectedDispatchConfigured,
  sendProtectedMessage,
} from "./firebase-dispatch";

export type ServiceRequestInput = {
  vehicle: string;
  problem: string;
  locationLabel: string;
  coordinates?: { latitude: number; longitude: number } | null;
};

export type TripRecord = {
  id: string;
  problem: string;
  vehicle: string;
  status: string;
  locationLabel: string;
  price?: number;
  helperName?: string;
};

export type TripResumeDetails = TripRecord & {
  coordinates?: { latitude: number; longitude: number } | null;
};

export type ServiceRequestRecord = {
  id: string;
  status: string;
  partnerId?: string;
  helperName?: string;
  helperInitials?: string;
  helperRating?: number;
  helperJobs?: number;
  price?: number;
  etaMinutes?: number;
  partnerDistanceKm?: number;
  partnerCoordinates?: { latitude: number; longitude: number };
  paymentMethod?: string;
  paymentStatus?: string;
  jobCode?: string;
  paidAtMs?: number;
};

export type TrackingPoint = {
  latitude: number;
  longitude: number;
  accuracy?: number;
  heading?: number;
  updatedAt?: unknown;
};

export type MechanicRecord = {
  id: string;
  name: string;
  eta: string;
  distance: string;
  rating: string;
  jobs: string;
  price: number;
  color: string;
  initials: string;
};

export type PaymentMethod = "cash" | "upi_on_service";

export const POLICY_VERSION = "2026-07-29";

export function watchAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Firebase restores the saved session asynchronously, so `auth.currentUser` is null for the
 * first moments after boot. Anything that may create a guest must wait here first, otherwise
 * it signs the user out of their real account and replaces it with a fresh anonymous one.
 */
async function authReady() {
  await auth.authStateReady();
  await consumeGoogleRedirectResult();
  return auth.currentUser;
}

export async function ensureGuestSession() {
  const restored = await authReady();
  if (restored) return restored;
  return (await signInAnonymously(auth)).user;
}

export async function registerWithEmail(email: string, password: string) {
  await authReady();
  if (auth.currentUser?.isAnonymous) {
    try {
      return (await linkWithCredential(auth.currentUser, EmailAuthProvider.credential(email, password))).user;
    } catch (error) {
      if (!isAlreadyRegisteredError(error)) throw error;
    }
  }
  try {
    return (await createUserWithEmailAndPassword(auth, email, password)).user;
  } catch (error) {
    if (!isAlreadyRegisteredError(error)) throw error;
    return (await signInWithEmailAndPassword(auth, email, password)).user;
  }
}

export async function loginWithEmail(email: string, password: string) {
  await authReady();
  return (await signInWithEmailAndPassword(auth, email, password)).user;
}

function googleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

function errorCode(error: unknown) {
  return String((error as { code?: string }).code || "");
}

function isPopupBlockedError(error: unknown) {
  const code = errorCode(error);
  const message = String((error as { message?: string }).message || "");
  return (
    code.includes("popup-blocked")
    || code.includes("popup-closed")
    || code.includes("cancelled-popup")
    || code.includes("operation-not-supported")
    || /cross-origin-opener-policy|window\.closed|opener/i.test(message)
  );
}

function isAlreadyRegisteredError(error: unknown) {
  const code = errorCode(error);
  return code.includes("email-already-in-use") || code.includes("credential-already-in-use");
}

const AUTH_MESSAGES: Record<string, string> = {
  "auth/invalid-email": "That email address is not valid",
  "auth/invalid-credential": "Wrong email or password",
  "auth/wrong-password": "Wrong email or password",
  "auth/user-not-found": "No account with that email — create one first",
  "auth/user-disabled": "This account has been disabled",
  "auth/email-already-in-use": "Account exists — switch to sign in",
  "auth/weak-password": "Use 8+ characters with upper, lower and a number",
  "auth/too-many-requests": "Too many attempts — wait a minute and retry",
  "auth/network-request-failed": "Network blocked sign-in — check your internet or DNS and retry",
  "auth/popup-blocked": "Allow pop-ups for this site, then tap Google again",
  "auth/popup-closed-by-user": "Pop-up closed before sign-in finished",
  "auth/unauthorized-domain": "This domain is not authorised in Firebase Auth",
  "auth/operation-not-allowed": "This sign-in method is switched off in Firebase Auth",
  "auth/account-exists-with-different-credential": "Use the email and password you signed up with",
};

/** Turns a Firebase auth failure into something the user can actually act on. */
export function authErrorMessage(error: unknown, fallback: string) {
  const code = errorCode(error);
  if (AUTH_MESSAGES[code]) return AUTH_MESSAGES[code];
  return code ? `${fallback} (${code.replace("auth/", "")})` : fallback;
}

let pendingRedirect: Promise<User | null> | null = null;

/** Completes Google redirect sign-in after the page reloads. Safe to call more than once. */
export function consumeGoogleRedirectResult() {
  if (!pendingRedirect) {
    pendingRedirect = getRedirectResult(auth)
      .then((result) => result?.user || null)
      .catch(() => null);
  }
  return pendingRedirect;
}

/** Resolves to null when the browser is being sent to Google for a full-page redirect. */
export async function loginWithGoogle(): Promise<User | null> {
  await authReady();
  const provider = googleProvider();
  const guest = auth.currentUser?.isAnonymous ? auth.currentUser : null;
  if (guest) {
    try {
      return (await linkWithPopup(guest, provider)).user;
    } catch (error) {
      // The Google account already owns a profile. Reuse the credential we just got so the
      // user is not asked to approve a second pop-up (browsers block that one).
      if (isAlreadyRegisteredError(error)) {
        const credential = GoogleAuthProvider.credentialFromError(error as AuthError);
        if (credential) return (await signInWithCredential(auth, credential)).user;
      } else if (isPopupBlockedError(error)) {
        await linkWithRedirect(guest, provider);
        return null;
      } else {
        throw error;
      }
    }
  }
  try {
    return (await signInWithPopup(auth, provider)).user;
  } catch (error) {
    if (!isPopupBlockedError(error)) throw error;
    // COOP / popup blockers: full-page redirect is reliable on Firebase Hosting.
    await signInWithRedirect(auth, provider);
    return null;
  }
}

export async function logoutUser() {
  await signOut(auth);
  return ensureGuestSession();
}

export function resetPassword(email: string) {
  return sendPasswordResetEmail(auth, email);
}

export async function sendVerificationEmail() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error("Sign in first");
  await sendEmailVerification(user, {
    url: "https://mistry-bhai.web.app",
    handleCodeInApp: false,
  });
}

export async function refreshCurrentUser() {
  if (!auth.currentUser) return null;
  await reload(auth.currentUser);
  return auth.currentUser;
}

export async function saveUserProfile(user: User) {
  const profileRef = doc(db, "users", user.uid);
  const existing = await getDoc(profileRef);
  await setDoc(profileRef, {
    displayName: user.displayName || "",
    email: (user.email || "").toLowerCase(),
    isAnonymous: user.isAnonymous,
    updatedAt: serverTimestamp(),
    // Never demote an existing partner/admin on routine profile sync.
    ...(existing.exists() ? {} : { role: "customer" }),
  }, { merge: true });
}

export async function createServiceRequest(input: ServiceRequestInput) {
  const user = await ensureGuestSession();
  const ref = await addDoc(collection(db, "serviceRequests"), {
    customerId: user.uid,
    ...input,
    status: "broadcasting",
    searchRadiusKm: 8,
    contactUnlocked: false,
    currency: "INR",
    policyVersion: POLICY_VERSION,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** Cancels leftover SOS broadcasts from this customer so old test requests stop appearing to mistris. */
export async function cancelMyBroadcastingRequests() {
  const user = await ensureGuestSession();
  const snapshot = await getDocs(query(
    collection(db, "serviceRequests"),
    where("customerId", "==", user.uid),
    orderBy("createdAt", "desc"),
    limit(20),
  ));
  await Promise.all(snapshot.docs
    .filter((entry) => String(entry.get("status") || "") === "broadcasting")
    .map((entry) => updateDoc(entry.ref, {
      status: "cancelled",
      cancelReason: "replaced_by_new_request",
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })));
}

export async function sendRequestMessage(requestId: string, text: string) {
  if (messageHasContactLeak(text)) {
    throw new Error("Contact details and direct-payment links are blocked for your protection.");
  }
  if (protectedDispatchConfigured) {
    await sendProtectedMessage(requestId, text);
    return;
  }
  const user = await ensureGuestSession();
  const profile = await getDoc(doc(db, "users", user.uid));
  const role = profile.exists() && profile.get("role") === "partner" ? "partner" : "customer";
  await addDoc(collection(db, "serviceRequests", requestId, "messages"), {
    senderId: user.uid,
    senderRole: role,
    text,
    createdAt: serverTimestamp(),
  });
}

export function watchServiceRequest(
  requestId: string,
  callback: (request: ServiceRequestRecord) => void,
) {
  return onSnapshot(doc(db, "serviceRequests", requestId), (snapshot) => {
    if (!snapshot.exists()) return;
    const data = snapshot.data();
    const partnerLocation = data.partnerLocation as { latitude?: unknown; longitude?: unknown } | undefined;
    callback({
      id: snapshot.id,
      status: String(data.status || "broadcasting"),
      partnerId: data.partnerId ? String(data.partnerId) : undefined,
      helperName: data.helperName ? String(data.helperName) : undefined,
      helperInitials: data.helperInitials ? String(data.helperInitials) : undefined,
      helperRating: typeof data.helperRating === "number" ? data.helperRating : undefined,
      helperJobs: typeof data.helperJobs === "number" ? data.helperJobs : undefined,
      price: typeof data.price === "number" ? data.price : undefined,
      etaMinutes: typeof data.etaMinutes === "number" ? data.etaMinutes : undefined,
      partnerDistanceKm: typeof data.partnerDistanceKm === "number" ? data.partnerDistanceKm : undefined,
      partnerCoordinates: typeof partnerLocation?.latitude === "number" && typeof partnerLocation.longitude === "number"
        ? { latitude: partnerLocation.latitude, longitude: partnerLocation.longitude }
        : undefined,
      paymentMethod: data.paymentMethod ? String(data.paymentMethod) : undefined,
      paymentStatus: data.paymentStatus ? String(data.paymentStatus) : undefined,
      jobCode: data.jobCode ? String(data.jobCode) : undefined,
      paidAtMs: (data.paidAt as { toMillis?: () => number } | undefined)?.toMillis?.() || undefined,
    });
  });
}

export function watchRequestTracking(
  requestId: string,
  callback: (point: TrackingPoint | null) => void,
) {
  // Prefer partner UID docs (client dispatch). Fall back to legacy "current" doc
  // used by the Cloud Functions path.
  return onSnapshot(collection(db, "serviceRequests", requestId, "tracking"), (snapshot) => {
    const partnerDoc = snapshot.docs.find((item) => item.id !== "current" && item.data().role === "partner")
      || snapshot.docs.find((item) => item.id === "current")
      || snapshot.docs[0];
    if (!partnerDoc) {
      callback(null);
      return;
    }
    const data = partnerDoc.data();
    if (typeof data.latitude !== "number" || typeof data.longitude !== "number") {
      callback(null);
      return;
    }
    callback({
      latitude: data.latitude,
      longitude: data.longitude,
      accuracy: typeof data.accuracy === "number" ? data.accuracy : undefined,
      heading: typeof data.heading === "number" ? data.heading : undefined,
      updatedAt: data.updatedAt,
    });
  });
}

export function watchRequestMessages(
  requestId: string,
  callback: (messages: Array<{ id: string; senderId: string; text: string }>) => void,
) {
  const messagesQuery = query(
    collection(db, "serviceRequests", requestId, "messages"),
    orderBy("createdAt", "asc"),
    limit(100),
  );
  return onSnapshot(messagesQuery, (snapshot) => {
    callback(snapshot.docs.map((message) => ({
      id: message.id,
      senderId: String(message.data().senderId || ""),
      text: String(message.data().text || ""),
    })));
  });
}

function tripFromDoc(snapshot: QueryDocumentSnapshot<DocumentData>): TripRecord {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    problem: String(data.problem || "Roadside help"),
    vehicle: String(data.vehicle || "Vehicle"),
    status: String(data.status || "requested"),
    locationLabel: String(data.locationLabel || "Current location"),
    price: typeof data.price === "number" ? data.price : undefined,
    helperName: data.helperName ? String(data.helperName) : undefined,
  };
}

export async function getMyTrips() {
  const user = await ensureGuestSession();
  const tripsQuery = query(
    collection(db, "serviceRequests"),
    where("customerId", "==", user.uid),
    orderBy("createdAt", "desc"),
    limit(12),
  );
  return (await getDocs(tripsQuery)).docs.map(tripFromDoc);
}

export function watchMyTrips(callback: (trips: TripRecord[]) => void) {
  let innerUnsub: (() => void) | null = null;
  void ensureGuestSession().then((user) => {
    const tripsQuery = query(
      collection(db, "serviceRequests"),
      where("customerId", "==", user.uid),
      orderBy("createdAt", "desc"),
      limit(12),
    );
    innerUnsub = onSnapshot(tripsQuery, (snapshot) => {
      callback(snapshot.docs.map(tripFromDoc));
    });
  });
  return () => innerUnsub?.();
}

export async function fetchTripResumeDetails(tripId: string): Promise<TripResumeDetails | null> {
  const snap = await getDoc(doc(db, "serviceRequests", tripId));
  if (!snap.exists()) return null;
  const trip = tripFromDoc(snap as QueryDocumentSnapshot<DocumentData>);
  const data = snap.data();
  const coords = data.coordinates as { latitude?: unknown; longitude?: unknown } | undefined;
  return {
    ...trip,
    coordinates: typeof coords?.latitude === "number" && typeof coords?.longitude === "number"
      ? { latitude: coords.latitude, longitude: coords.longitude }
      : null,
  };
}

export function watchVerifiedMechanics(callback: (mechanics: MechanicRecord[]) => void) {
  const mechanicQuery = query(
    collection(db, "mechanics"),
    where("verified", "==", true),
    where("online", "==", true),
    limit(20),
  );
  return onSnapshot(mechanicQuery, (snapshot) => {
    const palette = ["#ffcf40", "#8cdf9a", "#85b8ff", "#ef9fc5"];
    callback(snapshot.docs.map((mechanic, index) => {
      const data = mechanic.data();
      const completedJobs = Number(data.completedJobs || 0);
      return {
        id: mechanic.id,
        name: String(data.name || "Mistry Bhai Partner"),
        eta: `${Number(data.etaMinutes || 12)} min`,
        distance: `${(1.2 + index * 0.7).toFixed(1)} km`,
        rating: Number(data.rating || 4.7).toFixed(1),
        jobs: completedJobs >= 1000 ? `${(completedJobs / 1000).toFixed(1)}k` : String(completedJobs),
        price: Number(data.basePrice || 299),
        color: palette[index % palette.length],
        initials: String(data.initials || "MB"),
      };
    }));
  });
}

export type PartnerApplicationInput = {
  name: string;
  phone: string;
  email: string;
  dateOfBirth: string;
  alternatePhone: string;
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
  shopName: string;
  experienceYears: number;
  services: string[];
  vehicleTypes: string[];
  workType: "mobile" | "workshop" | "both";
  serviceRadiusKm: number;
  idType: "aadhaar" | "dl" | "voter" | "pan";
  idNumberMasked: string;
  panNumber: string;
  dlNumber: string;
  gstin: string;
  accountHolderName: string;
  accountNumberMasked: string;
  ifsc: string;
  upiId: string;
  emergencyName: string;
  emergencyPhone: string;
  idDocumentPath: string;
  selfiePath: string;
  workshopPhotoPath: string;
  idDocSource: "camera" | "file";
  selfieSource: "camera" | "file";
  acceptedPartnerCode: boolean;
  autoVerified: boolean;
  verificationChecks: Array<{ key: string; label: string; ok: boolean }>;
};

export async function submitPartnerApplication(input: PartnerApplicationInput) {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error("Sign in with email or Google before applying as a partner");
  }
  const auto = input.autoVerified === true;
  await setDoc(doc(db, "partnerApplications", user.uid), {
    applicantId: user.uid,
    ...input,
    status: auto ? "approved" : "submitted",
    verificationStatus: auto ? "auto_approved" : "pending_review",
    policyVersion: POLICY_VERSION,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(auto ? { reviewedAt: serverTimestamp(), reviewedBy: "system_auto_verify" } : {}),
  }, { merge: true });
}

/** After secure auto-verification, provision the live mechanic profile. */
export async function activateAutoVerifiedPartner(input: {
  name: string;
  basePrice?: number;
  city?: string;
  services?: string[];
  vehicleTypes?: string[];
  workType?: string;
  serviceRadiusKm?: number;
  upiId?: string;
}) {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error("Sign in required");
  const appSnap = await getDoc(doc(db, "partnerApplications", user.uid));
  if (!appSnap.exists() || !["auto_approved", "approved"].includes(String(appSnap.get("verificationStatus")))) {
    throw new Error("KYC is not auto-approved yet");
  }
  const name = input.name.slice(0, 80);
  const initials = name.split(/\s+/).map((w) => w[0]).join("").slice(0, 3).toUpperCase() || "MB";
  const existingUser = await getDoc(doc(db, "users", user.uid));
  const existingRole = existingUser.exists() ? String(existingUser.get("role") || "customer") : "customer";
  const keepStaff = ["owner", "admin", "support"].includes(existingRole);
  await setDoc(doc(db, "users", user.uid), {
    ...(keepStaff ? {} : { role: "partner" }),
    displayName: name,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  // Don't store UPI on public mechanics doc — keep payout on application only.
  await setDoc(doc(db, "mechanics", user.uid), {
    name,
    initials,
    basePrice: Math.max(49, Math.min(100000, Math.round(input.basePrice || 299))),
    rating: 4.7,
    completedJobs: 0,
    online: false,
    verified: true,
    suspended: false,
    subscriptionStatus: "active",
    city: input.city || "",
    services: input.services || [],
    vehicleTypes: input.vehicleTypes || [],
    workType: input.workType || "mobile",
    serviceRadiusKm: input.serviceRadiusKm || 10,
    approvedAt: serverTimestamp(),
    approvedBy: "system_auto_verify",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function createSafetyAlert(locationLabel: string, requestId?: string) {
  const user = await ensureGuestSession();
  await addDoc(collection(db, "safetyAlerts"), {
    customerId: user.uid,
    requestId: requestId || null,
    locationLabel,
    status: "open",
    createdAt: serverTimestamp(),
  });
}

export async function recordConsent(purpose: "account" | "booking" | "partner") {
  const user = await ensureGuestSession();
  await addDoc(collection(db, "users", user.uid, "consents"), {
    subjectId: user.uid,
    purpose,
    policyVersion: POLICY_VERSION,
    granted: true,
    grantedAt: serverTimestamp(),
  });
}

export async function createPaymentIntent(input: {
  requestId: string;
  amount: number;
  method: PaymentMethod;
}) {
  const user = await ensureGuestSession();
  const ref = await addDoc(collection(db, "paymentIntents"), {
    customerId: user.uid,
    requestId: input.requestId,
    amount: input.amount,
    currency: "INR",
    method: input.method,
    status: "pending",
    provider: input.method === "cash" ? "offline_cash" : "partner_upi",
    policyVersion: POLICY_VERSION,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function submitSupportTicket(input: {
  category: string;
  message: string;
  requestId?: string;
}) {
  const user = await ensureGuestSession();
  const ref = await addDoc(collection(db, "supportTickets"), {
    customerId: user.uid,
    requestId: input.requestId || null,
    category: input.category,
    message: input.message,
    status: "open",
    priority: input.category === "safety" ? "urgent" : "normal",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function submitPrivacyRequest(
  type: "access" | "correction" | "deletion",
  details: string,
) {
  const user = await ensureGuestSession();
  if (user.isAnonymous) throw new Error("Account required");
  await addDoc(collection(db, "privacyRequests"), {
    subjectId: user.uid,
    type,
    details,
    status: "received",
    policyVersion: POLICY_VERSION,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
